import type { Lane, TrackData } from "../types";

/**
 * Single source of truth for track shape tuning. The three lanes are exact
 * parallel offsets of ONE master curve (spine + shared weave) — tune the
 * curve here and all lanes follow uniformly.
 */
export const TUNING = {
  /** Horizontal speed in px/s at intensity 0.5. */
  baseSpeed: 430,
  /** How much speed swings with intensity (0 = constant speed). */
  speedSwing: 0.62,
  /** Peak height of hills/valleys from the intensity envelope, px. */
  heightAmplitude: 380,
  /** Vertical distance between lanes, px. */
  laneGap: 78,
  /** Spatial frequency of the fine weave (radians per px). */
  weaveFreq: 0.005,
  /** Weave amplitude at intensity 0 / 1, px. */
  weaveAmpMin: 10,
  weaveAmpMax: 26,
};

export const LANE_GAP = TUNING.laneGap;

/**
 * Precomputed world geometry: per analysis frame, a world (x, y) point for
 * the track spine. Intense sections scroll faster (Audiosurf downhill rush).
 */
export class TrackGeometry {
  readonly xs: Float32Array;
  readonly ys: Float32Array;
  readonly frameRate: number;
  readonly numFrames: number;
  readonly totalLength: number;

  constructor(readonly data: TrackData) {
    this.frameRate = data.frameRate;
    this.numFrames = data.height.length;
    this.xs = new Float32Array(this.numFrames);
    this.ys = new Float32Array(this.numFrames);
    const dt = 1 / data.frameRate;
    let x = 0;
    for (let i = 0; i < this.numFrames; i++) {
      const intensity = data.intensity[i]!;
      const speed = TUNING.baseSpeed * (1 - TUNING.speedSwing / 2 + TUNING.speedSwing * intensity);
      this.xs[i] = x;
      this.ys[i] = -data.height[i]! * TUNING.heightAmplitude;
      x += speed * dt;
    }
    this.totalLength = x;
  }

  /** Fractional frame index for a song time. */
  frameAt(time: number): number {
    return Math.min(this.numFrames - 1.001, Math.max(0, time * this.frameRate));
  }

  xAt(time: number): number {
    const f = this.frameAt(time);
    const i = Math.floor(f);
    const frac = f - i;
    return this.xs[i]! + (this.xs[Math.min(this.numFrames - 1, i + 1)]! - this.xs[i]!) * frac;
  }

  /** Spine Y at fractional frame index. */
  spineYAtFrame(f: number): number {
    const i = Math.floor(f);
    const frac = f - i;
    const j = Math.min(this.numFrames - 1, i + 1);
    return this.ys[i]! + (this.ys[j]! - this.ys[i]!) * frac;
  }

  /**
   * Master curve: spine + shared fine weave. All lanes derive from this,
   * so the three tracks always move as one uniform shape.
   */
  masterYAtFrame(f: number): number {
    const i = Math.min(this.numFrames - 1, Math.floor(f));
    const x = this.xs[i]!;
    const intensity = this.data.intensity[i]!;
    const amp = TUNING.weaveAmpMin + (TUNING.weaveAmpMax - TUNING.weaveAmpMin) * intensity;
    return this.spineYAtFrame(f) + Math.sin(x * TUNING.weaveFreq) * amp;
  }

  /** Lane Y at fractional frame index. Lane 0 bottom, 2 top — exact parallel offsets. */
  laneYAtFrame(f: number, lane: Lane): number {
    return this.masterYAtFrame(f) + (1 - lane) * TUNING.laneGap;
  }

  laneYAt(time: number, lane: Lane): number {
    return this.laneYAtFrame(this.frameAt(time), lane);
  }

  /** Track slope in radians at a song time (for tilting the player cube). */
  slopeAt(time: number): number {
    const f = this.frameAt(time);
    const i = Math.max(1, Math.min(this.numFrames - 2, Math.round(f)));
    const dx = this.xs[i + 1]! - this.xs[i - 1]!;
    const dy = this.ys[i + 1]! - this.ys[i - 1]!;
    return Math.atan2(dy, dx);
  }

  /** First frame index whose x >= the given world x (binary search). */
  frameForX(x: number): number {
    let lo = 0;
    let hi = this.numFrames - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.xs[mid]! < x) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }
}
